<template>
  <div id="main-content" class="feature-landing-page">
    <Alert v-if="apiStatus.submitClaim.fail" alertType="error">
      <p>
        Sorry we weren't able to save your request. Please try submitting it again. If you've already done that please contact us on
        <a href="tel:1800 335 425">1800 335 425</a>
        . Our friendly team will be happy to help you.
      </p>
    </Alert>
    <h1>{{ $route.meta.title }}</h1>

    <div
      :class="dragDropZoneClass"
      @dragenter.prevent="toggleDragZoneActive(true)"
      @dragleave.prevent="toggleDragZoneActive(false)"
      @dragover.prevent
      @drop.prevent="addDroppedFile"
    >
      <!-- prettier-ignore -->
      <p v-if="currentDevice === DeviceTypes.DESKTOP">
        Drag files here or <label class="non-button-style" for="selectFiles">browse</label>
      </p>
      <label v-if="currentDevice === DeviceTypes.MOBILE || currentDevice === DeviceTypes.TABLET" class="button secondary" for="selectFiles">Browse</label>
      <input
        ref="snap-and-send-file-uploader"
        aria-label="File uploader"
        type="file"
        id="selectFiles"
        @change="addSelectedFile"
        accept="image/png, image/jpg, .gif, .pdf"
        multiple
      />
      <p v-if="currentDevice === DeviceTypes.MOBILE || currentDevice === DeviceTypes.TABLET">OR</p>
      <label v-if="currentDevice === DeviceTypes.MOBILE || currentDevice === DeviceTypes.TABLET" class="button secondary" capture="environment" for="imageTaken">
        Take a photo
      </label>

      <input aria-label="Take photo" id="imageTaken" type="file" accept="image/png, image/jpg, .gif, .pdf" @change="addSelectedFile" />
    </div>
    <div class="center-section">
      <div v-if="files.length > 0" class="file-previewer">
        <div v-for="(file, index) in files" :key="`${file.name}-${index}`" class="uploaded-files">
          <div class="file-container" :id="`${index}-selected-container`">
            <div v-if="(v$.files.$each.$response?.$errors?.[index] || []).some(e => e.$validator === 'isValidFileUploadSize' || e.$validator === 'isValidFileUploadType')" class="file upload-error">
              <div class="file-block">
                <span class="file-name">{{ file.name }}</span>
                <span v-if="(v$.files.$each.$response?.$errors?.[index] || []).some(e => e.$validator === 'isValidFileUploadType')" class="error-status">
                  The file type you have selected is not accepted. We accept .jpg, .png, .gif and .pdf files. Please remove this file and upload another document.
                </span>
                <span v-else-if="(v$.files.$each.$response?.$errors?.[index] || []).some(e => e.$validator === 'isValidFileUploadSize')" class="error-status">
                  This document is too big to be uploaded. Individual documents cannot exceed 32MB.
                </span>
              </div>
              <a
                v-show="fileStatus[index] !== LoadingStatus.PENDING"
                role="button"
                class="remove-file"
                :aria-label="`Remove file: ${file.name}`"
                @click.prevent="removeSelectedFile(index)"
                href="#"
              ></a>
            </div>
            <div v-else class="file" :class="uploadStatusClass">
              <div
                v-if="fileStatus[index] !== LoadingStatus.PENDING && fileStatus[index] !== LoadingStatus.SUCCESS && !['image/jpeg', 'image/png'].includes(fileTypes[index])"
                class="default"
              ></div>
              <img
                v-if="fileStatus[index] !== LoadingStatus.PENDING && fileStatus[index] !== LoadingStatus.SUCCESS && ['image/jpeg', 'image/png'].includes(fileTypes[index])"
                :src="fileImgs[index]"
                class="preview"
              />
              <div v-if="fileStatus[index] === LoadingStatus.SUCCESS" class="uploaded"></div>

              <Spinner v-if="fileStatus[index] === LoadingStatus.PENDING" class="spinner" />
              <span v-if="fileStatus[index] === LoadingStatus.PENDING" class="uploading">Uploading</span>

              <div class="file-block">
                <span v-if="fileStatus[index] !== LoadingStatus.PENDING" class="file-name">{{ file.name }}</span>
                <span v-if="fileStatus[index] !== LoadingStatus.PENDING && fileStatus[index] !== LoadingStatus.SUCCESS" class="upload-status">Not yet uploaded</span>
                <span v-if="fileStatus[index] === LoadingStatus.SUCCESS" class="upload-status">Uploaded successfully</span>
              </div>
              <a
                v-show="fileStatus[index] !== LoadingStatus.PENDING && fileStatus[index] !== LoadingStatus.SUCCESS"
                role="button"
                class="remove-file"
                :aria-label="`Remove file: ${file.name}`"
                @click.prevent="removeSelectedFile(index)"
                href="#"
              ></a>
            </div>
          </div>
        </div>
        <Alert :for="id" v-if="v$.files.$error && v$.files.$errors.some(e => e.$validator === 'isValidTotalFileUploadSize')" alertType="error" class="inline-message">
          <div>
            <p>The total of all documents cannot exceed 128MB. Please try again using the allowed file types - *.jpg, *.png, *.gif. *.pdf</p>
          </div>
        </Alert>
      </div>
      <p>We accept .jpg, .png, .gif and .pdf files up to 32MB. There is no limit on the number of files you can upload, but all files cannot exceed 128 MB.</p>
      <div class="grey-box-expanded">
        <ApiLoading v-if="apiStatus.submitClaim.loading" :apiError="apiStatus.submitClaim.fail" class="loading-wrapper" :loadingMessage="loadingMessage" isMemberPortal />

        <button v-if="!apiStatus.submitClaim.loading" class="button" :disabled="v$.$invalid || !(files.length > 0)" @click="submitClaim()">Submit claim</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { VUEX_GENERAL } from '@/constants/VuexConstants';
import { LoadingStatus } from '@general/js/enums';
import { isValidFileUploadSize, isValidTotalFileUploadSize, isValidFileUploadType } from '@general/js/validators';
import { SNAP_AND_SEND_RESULT } from '@/router/routeNames';
import { Alert, ApiLoading } from '@/components';
import ClaimsServices from '@/services/Claims';
import { deviceType } from '@/utils';
import { DHLError } from '@general/js/utils';
import Spinner from '@general/js/components/Spinner.vue';
import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { DeviceTypes, FileUploadCategoryTypes } from '@/enums';

export default {
  setup() { return { v$: useVuelidate() }; },

  name: 'SnapAndSend',

  components: {
    Alert,
    ApiLoading,
    Spinner,
  },

  data() {
    return {
      files: [],
      fileImgs: [],
      totalFileSize: 0,
      LoadingStatus,
      DeviceTypes,
      fileTypes: [],
      fileNames: [],
      fileStatus: [],
      blobNames: [],
      fileInvalid: false,
      apiStatus: {
        uploadFile: {
          loading: false,
        },
        submitClaim: {
          fail: false,
          loading: false,
        },
      },
      dropzoneActive: false,
      dateTimestamp: null,
      uploadFileProgress: 0,
      currentDevice: null,
      submitted: false,
    };
  },

  validations: {
    files: {
      $each: helpers.forEach({
        isValidFileUploadSize,
        isValidFileUploadType,
      }),
      isValidTotalFileUploadSize,
    },
  },

  beforeMount() {
    this.currentDevice = deviceType();
  },

  mounted() {
    const dateNow = new Date();
    this.dateTimestamp = dateNow.toISOString();

    window.addEventListener('beforeunload', this.preventLeavePage);
  },

  beforeRouteLeave(to, from, next) {
    if (!this.confirmLeave()) {
      next(false);
    } else {
      next();
    }
  },

  beforeUnmount() {
    window.removeEventListener('beforeunload', this.preventLeavePage);
  },

  computed: {
    ...mapGetters({
      policyNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      personCode: VUEX_GENERAL.GETTERS.getCurrentPersonCode,
      currentIsExtrasFlag: VUEX_GENERAL.GETTERS.getCurrentPolicyIsExtrasFlag,
    }),

    loadingMessage() {
      let message = '';
      if (this.apiStatus.uploadFile.loading) {
        message = message.concat(`${this.uploadFileProgress}/${this.files.length} files have been uploaded. <br>`);
      }
      message = message.concat(`We are processing your request. It shouldn't be too long.`);

      return message;
    },

    uploadStatusClass(index) {
      const status = this.fileStatus[index];
      if (status === LoadingStatus.PENDING) {
        return 'uploading';
      }
      if (status === LoadingStatus.SUCCESS) {
        return 'uploaded';
      }
      return '';
    },

    dragDropZoneClass() {
      let className = 'snap-and-send-container';
      if (this.currentDevice === DeviceTypes.DESKTOP) {
        if (this.dropzoneActive) {
          className += ' active';
        } else {
          className += ' dashed';
        }
      }
      return className;
    },
  },

  methods: {
    toggleDragZoneActive(value) {
      this.dropzoneActive = value;
    },

    addFiles(selectedFiles) {
      Array.from(selectedFiles).forEach((file) => {
        this.files.push(file);
        this.totalFileSize += file.size;
        this.fileImgs.push(URL.createObjectURL(file));
        this.fileTypes.push(file.type);
        this.fileNames.push(file.name);
        this.fileStatus.push(null);
      });
      this.v$.files.$touch();

      this.$refs['snap-and-send-file-uploader'].value = '';
    },

    addSelectedFile(event) {
      const selected = event.target.files;
      this.addFiles(selected);
    },

    addDroppedFile(event) {
      const droppedFiles = event.dataTransfer.files;
      this.addFiles(droppedFiles);

      this.toggleDragZoneActive(false);
    },

    removeSelectedFile(index) {
      const file = this.files[index];
      this.files.splice(index, 1);
      this.fileImgs.splice(index, 1);
      this.fileTypes.splice(index, 1);
      this.fileNames.splice(index, 1);
      this.fileStatus.splice(index, 1);
      this.totalFileSize -= file.size;
      this.v$.files.$touch();
    },

    /**
     * Uploads files from all claimlines
     * Goes through all claimlines and all files in each claim line to upload
     */
    async uploadFiles() {
      this.apiStatus.uploadFile.loading = true;
      let failed = false;
      for (let index = 0; index < this.files.length; index++) {
        if (this.fileStatus[index] !== LoadingStatus.SUCCESS) {
          this.fileStatus.splice(index, 1, LoadingStatus.PENDING);

          // eslint-disable-next-line no-await-in-loop
          const blobName = await this.uploadIndividualFile(this.files[index]);
          if (!blobName) {
            failed = true;
            this.fileStatus.splice(index, 1, null);
          } else {
            this.blobNames.push(blobName);
            this.fileStatus.splice(index, 1, LoadingStatus.SUCCESS);
          }
        }
      }
      this.apiStatus.uploadFile.loading = false;

      return failed;
    },

    async uploadIndividualFile(file) {
      try {
        const response = await ClaimsServices.submitFile(this.policyNumber, this.personCode, FileUploadCategoryTypes.SNAP_AND_SEND, file, this.dateTimestamp);
        if (response.blobName) {
          this.uploadFileProgress += 1;
          return response.blobName;
        }
        return false;
      } catch (err) {
        return false;
      }
    },

    async submitClaim() {
      this.apiStatus.submitClaim.loading = true;
      this.apiStatus.submitClaim.fail = false;
      try {
        const fileUploadFailed = await this.uploadFiles();
        if (fileUploadFailed) {
          throw new DHLError('File upload error');
        }
        const response = await ClaimsServices.submitSnapAndSend(this.policyNumber, this.dateTimestamp);
        if (response.status === 201) {
          this.submitted = true;
          // 201 created work item if successful
          this.$router.push({
            name: SNAP_AND_SEND_RESULT,
            state: { refData: this.dateTimestamp },
          });
        }
      } catch (error) {
        this.apiStatus.submitClaim.fail = true;
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
      } finally {
        this.apiStatus.submitClaim.loading = false;
      }
    },

    confirmLeave() {
      if (this.files.length === 0 || this.submitted) return true;
      // eslint-disable-next-line no-alert
      return window.confirm('Do you want to leave? Your unsaved changes will be lost.');
    },

    preventLeavePage(e) {
      // only stop refresh or close browser tab if user made some change without save
      if (this.files.length > 0 && !this.submitted) {
        // Cancel the event
        e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
        // Chrome requires returnValue to be set
        e.returnValue = '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.snap-and-send-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  background-color: $GREY_50;
  margin-bottom: 3.125rem;

  &.dashed {
    border: 3px dashed $GREY_600;
  }

  &.active {
    border: 5px dashed $GREY_600;
    background-color: $GREY_200;
  }

  padding: 50px;

  p {
    margin: 0;
    text-align: center;

    a {
      color: $CRIMSON;
      font-weight: $BOLD;
    }
  }

  button {
    text-align: center;
    min-width: 200px;
  }

  input {
    display: none;
  }
}

.center-section {
  display: grid;
  grid-template-columns: 1fr 3fr 1fr;
  grid-template-rows: 1fr;
  column-gap: $desktop_spacing;

  @include mobile {
    grid-template-columns: 1fr;
    column-gap: 0;
  }

  .file-previewer {
    grid-column: 2 / 3;
    grid-row: 1;
    margin-bottom: 1.25rem;

    .uploaded-files {
      display: flex;
      flex-direction: column;
      align-items: center;
      row-gap: 10px;
      grid-column: 2/3;
      margin-bottom: 10px;

      @include mobile {
        grid-column: 1;
      }

      .file-container {
        display: grid;
        grid-template-rows: 1fr;
        width: 100%;

        #selected-container {
          margin-top: 35px;
        }

        .file-name {
          color: $GREY_300;
          font-weight: unset;
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .file {
          box-shadow: 0px 1px 8px $GREY_300;
          display: grid;
          grid-template-columns: 62px 5fr 0.5fr;
          align-items: center;
          gap: 20px;

          &.uploading {
            grid-template-columns: 1fr 1fr 5fr 0.5fr;
          }

          .default {
            height: 100%;

            &::before {
              background-image: url('/assets/public/images/icon_file.svg');
              background-color: $GREY_300;
              background-size: 47%;
              content: '';
              display: block;
              background-repeat: no-repeat;
              min-height: 74px;
              height: 100%;
              width: 62px;
              background-position: center;

              @include tablet-and-below {
                margin-right: 20px;
              }
            }
          }

          .uploaded {
            &::before {
              background-image: url('/assets/public/images/icon_included.svg');
              background-size: 47%;
              content: '';
              display: block;
              background-repeat: no-repeat;
              height: 74px;
              width: 62px;
              background-position: center;

              @include tablet-and-below {
                margin-right: 20px;
              }
            }
          }

          &.upload-error {
            &::before {
              background-image: url('/assets/public/images/icon_error.svg');
              background-size: auto;
              background-color: $ERROR;
              content: '';
              display: block;
              background-repeat: no-repeat;
              height: 100%;
              width: 62px;
              background-position: center;
            }

            border: 1px solid $ERROR;
            font-weight: unset;
            overflow: hidden;
            text-overflow: ellipsis;

            .error-status {
              color: $ERROR;
              font-weight: unset;
              font-size: font-size(-1);
              grid-row: 2;
              overflow: hidden;
              text-overflow: ellipsis;
              min-width: 0;
              overflow-wrap: break-word;
            }
          }

          .preview {
            min-height: 74px;
            height: 100%;
            width: 62px;
            box-shadow: 0px 0px 10px $GREY_300;
            margin-right: 20px;
            display: block;
            background-position: center;
          }

          .uploading {
            font-weight: unset;
            margin-right: 5px;
          }

          .upload-status {
            justify-content: unset;
          }

          .file-block {
            display: grid;
            column-gap: 5px;
            row-gap: 5px;
            margin-right: 0;
            padding: 20px 0px;

            .file-name {
              color: unset;
              font-weight: $BOLD;
              grid-row: 1;
            }

            .upload-status {
              font-weight: unset;
              font-size: font-size(-1);
              grid-row: 2;
              grid-column: 1;
            }
          }

          a {
            $DIMENSION: 38px;

            &.remove-file {
              color: $GREY_600;
              height: $DIMENSION;
              min-width: $DIMENSION;
              line-height: $DIMENSION;
              background-color: transparent;
              font-size: font-size(3);
              font-weight: unset;
              right: 10px;
              text-align: center;
              text-decoration: none;

              &::before {
                content: '\00d7'; // This is the unicode for the multiplication symbol.
              }

              &:hover,
              &:focus {
                color: $CRIMSON;
                border-radius: 50%;
                background-color: $GREY_500;
              }

              &:active {
                color: $GREY_800;
                background-color: transparent;
              }
            }
          }
        }
      }
    }
  }

  p {
    grid-row: 2;
    grid-column: 2;
    margin-bottom: 3.125rem;
  }

  .grey-box-expanded {
    position: relative;
    padding: spacer(3) 0;
    grid-column: 2 / 3;
    grid-row: 3;
    display: flex;
    justify-content: flex-end;

    &:before {
      content: '';
      display: block;
      background: $GREY_50;
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      height: calc(100% + 250px);
      width: 100vw;
    }

    button {
      width: 245px;
      max-width: 245px;

      @include mobile {
        width: 100%;
        max-width: 100%;
      }
    }
  }
}

#main-content {
  .api-loading-wrapper {
    padding: 0px;
    min-height: unset;
    margin: auto;
  }
}
</style>
